/**
 * The search box on a RECORD TAB's list (docs/03 §3f, punch 3E.3).
 *
 * Matt, 2026-08-28: *"add a search box under property page > tenants and
 * property page > vendors, property page > notes, and using that same logic,
 * add it to tenants > notes, and also for the vendor pages."*
 *
 * "That same logic" is the point, and it is why this is a component rather than
 * five inputs. A record tab's list is small enough to read and just big enough
 * to scroll — a building with forty vendor rows, a tenant with sixty notes —
 * and the list SCREENS already have a search box with a settled shape. Five
 * hand-rolled copies is how one of them ends up without a visible focus ring,
 * which is exactly what happened to the list filters before docs/03 §3a
 * (`TenantsListScreen`'s were raw inputs carrying a copy of `INPUT_CLASSES`
 * with the ring dropped).
 *
 * FILTERED IN THE BROWSER, deliberately. These lists arrive whole with the
 * record — they are not paged, and there is no endpoint to ask — so a keystroke
 * costs nothing and a round trip would cost a spinner. The list screens filter
 * on the SERVER for the opposite reason: they page at 50, so filtering the page
 * would search only the page. Both rules follow from the same question, "is the
 * whole set here?", and this component is only for the case where it is.
 *
 * It renders NOTHING while the list is short. A search box over four rows is
 * noise that pushes the rows down; the threshold is a prop with a sane default,
 * and the box stays once a query is typed so a filter cannot hide its own
 * control by matching too well.
 */

import { useState, type ReactNode } from 'react';

import { TextField } from './fields.js';

/** Below this many rows, a search box costs more than it saves. */
export const TAB_SEARCH_MIN_ROWS = 6;

/**
 * Does this row match? Case- and accent-insensitive substring over the fields
 * the caller says are searchable, with an empty query matching everything.
 *
 * Substring rather than word-prefix: people search a suite number by its tail
 * ("204") and a vendor by a fragment of its name, and a prefix match refuses
 * both. Accents folded because a name typed without them should still find
 * "Núñez".
 */
export function matchesQuery(query: string, ...fields: (string | null | undefined)[]): boolean {
  const needle = fold(query);
  if (needle === '') return true;
  return fields.some((field) => fold(field ?? '').includes(needle));
}

function fold(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}+/gu, '');
}

/**
 * The box plus the count, as one control.
 *
 * `total` and `shown` are passed rather than derived because the caller owns
 * the filtering — this component must not be the thing that decides which rows
 * a tab shows, or two tabs will disagree about what "matching" means.
 */
export function TabSearch({
  label,
  value,
  onChange,
  total,
  shown,
  noun,
  minRows = TAB_SEARCH_MIN_ROWS,
}: {
  /** What is being searched, for the hidden label: "Search vendors". */
  readonly label: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly total: number;
  readonly shown: number;
  /** Plural noun for the count line: "vendors", "notes". */
  readonly noun: string;
  readonly minRows?: number;
}): ReactNode {
  if (total < minRows && value === '') return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <span className="grow sm:max-w-xs">
        <TextField
          label={label}
          labelHidden="always"
          type="search"
          value={value}
          placeholder={label}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      </span>
      {/* A live count, so a filter that matches nothing SAYS so rather than
          leaving an empty list to read as a broken tab. */}
      {value === '' ? null : (
        <span role="status" aria-live="polite" className="text-meta text-ink2">
          {shown === 0
            ? `No ${noun} match “${value.trim()}”`
            : `${String(shown)} of ${String(total)} ${noun}`}
        </span>
      )}
    </div>
  );
}

/** The state half, so a caller needs one line instead of four. */
export function useTabSearch(): { query: string; setQuery: (next: string) => void } {
  const [query, setQuery] = useState('');
  return { query, setQuery };
}
